/**
 * frontend/src/pages/ChangeRequestDetail.js
 * 
 * Detailed view of a single compliance change request.
 * Displays request information, current status, and a timeline of all activities (logs and comments).
 * Allows authorized users to update the status and add comments.
 * 
 * Common functions:
 * - loadData: Fetches change request details and associated activity logs.
 * - handleStatusChange: Updates the status of the change request.
 * - handleAddComment: Adds a new comment to the change request.
 * - getLogIcon/getLogIconStyle: Helper functions for rendering the activity timeline.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Form, Alert, Dropdown } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow, format } from 'date-fns';
import {
  FiArrowLeft, FiUser, FiClock, FiMessageCircle,
  FiSend, FiChevronDown, FiPlus, FiEdit, FiRefreshCw, FiUserCheck
} from 'react-icons/fi';
import DOMPurify from 'dompurify';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { changeRequestsAPI } from '../services/api';
import { formatErrorMessage } from '../utils/errorHandling';
import Sidebar from '../components/Layout/Sidebar';
import Header from '../components/Layout/Header';

const pageStyles = {
  pageWrapper: {
    minHeight: '100vh',
    background: '#f8fafc',
  },
  mainContent: {
    marginLeft: '72px',
    marginTop: '64px',
    minHeight: 'calc(100vh - 64px)',
    padding: '32px',
  },
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    color: '#64748b',
    textDecoration: 'none',
    fontSize: '0.9rem',
    marginBottom: '24px',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    border: '1px solid #e2e8f0',
  },
  loadingWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 'calc(100vh - 64px)',
  },
  activityIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.85rem',
  },
};

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_review', label: 'In Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'implemented', label: 'Implemented' },
];

const getLogIcon = (action) => {
  switch (action) {
    case 'created': return <FiPlus />;
    case 'status_changed': return <FiRefreshCw />;
    case 'commented': return <FiMessageCircle />;
    case 'updated': return <FiEdit />;
    case 'assigned': return <FiUserCheck />;
    default: return <FiMessageCircle />;
  }
};

const getLogIconStyle = (action) => {
  switch (action) {
    case 'created': return { background: '#d1fae5', color: '#10b981' };
    case 'status_changed': return { background: '#ede9fe', color: '#8b5cf6' };
    case 'commented': return { background: '#dbeafe', color: '#3b82f6' };
    case 'updated': return { background: '#fef3c7', color: '#f59e0b' };
    case 'assigned': return { background: '#fce7f3', color: '#ec4899' };
    default: return { background: '#f1f5f9', color: '#64748b' };
  }
};

const ChangeRequestDetail = () => {
  const { changeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentWorkspace, members, fetchWorkspace, fetchWorkspaces, fetchMembers, updateChangeRequest } = useWorkspace();

  const [change, setChange] = useState(null);
  const [logs, setLogs] = useState([]);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [commenting, setCommenting] = useState(false);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      let workspaceId = currentWorkspace?.id;
      if (!workspaceId) {
        const workspaces = await fetchWorkspaces();
        if (workspaces && workspaces.length > 0) {
          workspaceId = workspaces[0].id;
          await fetchWorkspace(workspaceId);
        } else {
          navigate('/dashboard');
          return;
        }
      }
      const [changeRes, logsRes] = await Promise.all([
        changeRequestsAPI.get(changeId),
        changeRequestsAPI.getLogs(changeId),
        fetchMembers(workspaceId),
      ]);
      setChange(changeRes.data);
      setLogs(logsRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load change request');
    } finally {
      setLoading(false);
    }
  }, [changeId, currentWorkspace, fetchWorkspace, fetchWorkspaces, fetchMembers, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleStatusChange = async (newStatus) => {
    try {
      const updated = await updateChangeRequest(change.id, { status: newStatus });
      setChange(updated);
      const logsRes = await changeRequestsAPI.getLogs(changeId);
      setLogs(logsRes.data);
    } catch (err) {
      setError(formatErrorMessage(err, 'Failed to update status'));
    }
  };

  const handleAssigneeChange = async (userId) => {
    try {
      const updated = await updateChangeRequest(change.id, { assigned_to: userId });
      setChange(updated);
      const logsRes = await changeRequestsAPI.getLogs(changeId);
      setLogs(logsRes.data);
    } catch (err) {
      setError(formatErrorMessage(err, 'Failed to update assignee'));
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;

    setCommenting(true);
    try {
      await changeRequestsAPI.addComment(changeId, comment);
      setComment('');
      const logsRes = await changeRequestsAPI.getLogs(changeId);
      setLogs(logsRes.data);
    } catch (err) {
      setError(formatErrorMessage(err, 'Failed to add comment'));
    } finally {
      setCommenting(false);
    }
  };

  if (loading) {
    return (
      <div style={pageStyles.pageWrapper}>
        <Sidebar workspace={currentWorkspace} />
        <Header workspace={currentWorkspace} />
        <main style={{ ...pageStyles.mainContent, ...pageStyles.loadingWrapper }}>
          <div className="spinner" style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e2e8f0',
            borderTopColor: '#6366f1',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </main>
      </div>
    );
  }

  if (!change) {
    return (
      <div style={pageStyles.pageWrapper}>
        <Sidebar workspace={currentWorkspace} />
        <Header workspace={currentWorkspace} />
        <main style={pageStyles.mainContent}>
          <div style={{ ...pageStyles.card, textAlign: 'center', padding: '60px' }}>
            <h5>Change request not found</h5>
            <Button variant="link" onClick={() => navigate('/dashboard')}>
              Go back to dashboard
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const isAdmin = currentWorkspace?.user_role === 'admin';

  return (
    <div style={pageStyles.pageWrapper}>
      <Sidebar workspace={currentWorkspace} />
      <Header workspace={currentWorkspace} />

      <main style={pageStyles.mainContent}>
        <motion.button
          style={pageStyles.backButton}
          onClick={() => navigate('/dashboard')}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ color: '#1e293b' }}
        >
          <FiArrowLeft />
          Back to Dashboard
        </motion.button>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <div className="row">
          {/* Main Content */}
          <div className="col-lg-8">
            <motion.div
              style={pageStyles.card}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Header */}
              <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                  <h3 className="fw-bold mb-2" style={{ color: '#1e293b' }}>{change.title}</h3>
                  <div className="d-flex gap-2 flex-wrap">
                    <span className={`type-badge type-${change.change_type}`}>
                      {change.change_type}
                    </span>
                    <span className={`impact-badge impact-${change.impact_level}`}>
                      {change.impact_level} impact
                    </span>
                  </div>
                </div>

                {isAdmin ? (
                  <Dropdown>
                    <Dropdown.Toggle
                      className={`status-badge status-${change.status} border-0`}
                      style={{ cursor: 'pointer' }}
                    >
                      {change.status.replace('_', ' ')}
                      <FiChevronDown className="ms-2" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {statusOptions.map((option) => (
                        <Dropdown.Item
                          key={option.value}
                          onClick={() => handleStatusChange(option.value)}
                          active={change.status === option.value}
                        >
                          {option.label}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                ) : (
                  <span className={`status-badge status-${change.status}`}>
                    {change.status.replace('_', ' ')}
                  </span>
                )}
              </div>

              {/* Meta */}
              <div className="d-flex gap-4 mb-4 text-muted small flex-wrap align-items-center">
                <div className="d-flex align-items-center">
                  <FiUser className="me-2" />
                  Requested by <strong className="ms-1">{change.requester_name}</strong>
                </div>
                <div className="d-flex align-items-center">
                  <FiClock className="me-2" />
                  {format(new Date(change.created_at), 'MMM d, yyyy h:mm a')}
                </div>
                <div className="d-flex align-items-center">
                  <FiUserCheck className="me-2" style={{ color: '#6366f1' }} />
                  <span className="me-1">Assigned to</span>
                  {isAdmin ? (
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="link"
                        className="p-0 text-decoration-none"
                        style={{
                          color: change.assignee_name ? '#6366f1' : '#94a3b8',
                          fontWeight: '600',
                          fontSize: '0.875rem',
                        }}
                      >
                        {change.assignee_name || 'Unassigned'}
                        <FiChevronDown className="ms-1" size={12} />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={() => handleAssigneeChange(0)}
                          active={!change.assigned_to}
                        >
                          Unassigned
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        {members.map((member) => (
                          <Dropdown.Item
                            key={member.user_id}
                            onClick={() => handleAssigneeChange(member.user_id)}
                            active={change.assigned_to === member.user_id}
                          >
                            {member.user_name}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  ) : (
                    <strong style={{ color: change.assignee_name ? '#6366f1' : '#94a3b8' }}>
                      {change.assignee_name || 'Unassigned'}
                    </strong>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="mb-0">
                <h6 className="fw-semibold mb-3" style={{ color: '#1e293b' }}>Description</h6>
                <div
                  className="text-muted"
                  style={{ whiteSpace: 'pre-wrap' }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(change.description) }}
                />
              </div>
            </motion.div>

            {/* Comment Form */}
            <motion.div
              style={pageStyles.card}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h6 className="fw-semibold mb-3" style={{ color: '#1e293b' }}>
                <FiMessageCircle className="me-2" />
                Add Comment
              </h6>
              <Form onSubmit={handleAddComment}>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Write a comment..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="mb-3"
                />
                <Button
                  type="submit"
                  style={{ background: '#6366f1', border: 'none' }}
                  disabled={commenting || !comment.trim()}
                >
                  {commenting ? (
                    <span className="spinner-border spinner-border-sm me-2" />
                  ) : (
                    <FiSend className="me-2" />
                  )}
                  Post Comment
                </Button>
              </Form>
            </motion.div>
          </div>

          {/* Activity Timeline */}
          <div className="col-lg-4">
            <motion.div
              style={pageStyles.card}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h6 className="fw-semibold mb-4" style={{ color: '#1e293b' }}>Activity Timeline</h6>

              <div>
                <AnimatePresence>
                  {logs.map((log, index) => (
                    <motion.div
                      key={log.id}
                      className="d-flex gap-3 pb-4"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      style={{
                        borderLeft: index < logs.length - 1 ? '2px solid #e2e8f0' : 'none',
                        marginLeft: '16px',
                        paddingLeft: '20px',
                        position: 'relative',
                      }}
                    >
                      <div
                        style={{
                          ...pageStyles.activityIcon,
                          ...getLogIconStyle(log.action),
                          position: 'absolute',
                          left: '-16px',
                          top: 0,
                        }}
                      >
                        {getLogIcon(log.action)}
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-semibold small">{log.user_name}</div>
                        <div className="small text-muted">
                          {log.action === 'created' && 'Created this change request'}
                          {log.action === 'status_changed' && (
                            <>
                              Changed status from{' '}
                              <span className={`status-badge status-${log.old_value} me-1`} style={{ padding: '2px 6px', fontSize: '0.65rem' }}>
                                {log.old_value?.replace('_', ' ')}
                              </span>
                              to{' '}
                              <span className={`status-badge status-${log.new_value}`} style={{ padding: '2px 6px', fontSize: '0.65rem' }}>
                                {log.new_value?.replace('_', ' ')}
                              </span>
                            </>
                          )}
                          {log.action === 'assigned' && (
                            <>
                              {log.old_value && log.old_value !== 'Unassigned' ? (
                                <>Reassigned from <strong>{log.old_value}</strong> to <strong style={{ color: '#6366f1' }}>{log.new_value}</strong></>
                              ) : (
                                <>Assigned to <strong style={{ color: '#6366f1' }}>{log.new_value}</strong></>
                              )}
                            </>
                          )}
                          {log.action === 'commented' && 'Added a comment'}
                          {log.action === 'updated' && log.new_value}
                        </div>
                        {log.comment && (
                          <div
                            className="mt-2 p-3 rounded small"
                            style={{ background: '#f8fafc' }}
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(log.comment) }}
                          />
                        )}
                        <div className="small text-muted mt-1">
                          {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ChangeRequestDetail;
